/**
 * Reading a Hydra collection.
 *
 * The core is an API Platform application and its collections come back in a Hydra envelope:
 *
 * ```json
 * { "@context": "…", "@id": "/api/v1/assets", "@type": "Collection",
 *   "totalItems": 214,
 *   "member": [ … ],
 *   "view": { "@id": "…?page=2", "first": "…", "last": "…?page=5", "next": "…?page=3" } }
 * ```
 *
 * The `application/json` representation of the same collection is a bare array. It carries the
 * rows and nothing else — no `totalItems`, so no page count — which is why `src/api/client.ts`
 * asks for `application/ld+json` and why the generated response types are the envelopes.
 *
 * Everything here is defensive about shape rather than trusting the generated type, for one
 * reason: these helpers also run against fixtures and against a core that may answer a
 * `?page=` beyond the last page with an envelope that has no `member` at all.
 */

/** The parts of the Hydra envelope this application reads. */
export interface HydraCollection<T> {
  member?: T[]
  totalItems?: number
  view?: {
    '@id'?: string
    first?: string | null
    last?: string | null
    previous?: string | null
    next?: string | null
  }
}

/** The rows, or `[]`. Never `undefined`: an absent `member` and an empty one read the same. */
export const members = <T>(collection: HydraCollection<T> | null | undefined): T[] =>
  Array.isArray(collection?.member) ? collection.member : []

/**
 * How many rows exist in total, or `null` when the core did not say.
 *
 * `null` is not zero. A collection without `totalItems` is one whose size we do not know, and a
 * paginator that renders "0" for that is lying — `CONVENTIONS.md` §5, null means unknown.
 */
export const totalItems = <T>(collection: HydraCollection<T> | null | undefined): number | null =>
  typeof collection?.totalItems === 'number' ? collection.totalItems : null

/**
 * The last page number, read out of the Hydra `view.last` IRI.
 *
 * Derived from the link rather than computed from `totalItems / itemsPerPage`, because the two
 * disagree the moment the core clamps `itemsPerPage` — and the link is what the core itself will
 * honour. Returns `null` when there is no pagination view, which is the honest answer for a
 * collection the core does not paginate (capabilities, for one).
 */
export const lastPage = <T>(collection: HydraCollection<T> | null | undefined): number | null => {
  const last = collection?.view?.last
  if (typeof last !== 'string') return null
  const match = /[?&]page=(\d+)/.exec(last)
  return match ? Number.parseInt(match[1], 10) : null
}

/** True when the core offered a link to a further page. */
export const hasNextPage = <T>(collection: HydraCollection<T> | null | undefined): boolean =>
  typeof collection?.view?.next === 'string' && collection.view.next !== ''

/**
 * Encode a repeatable filter for the core.
 *
 * The specification declares multi-valued filters — `kind`, `tag`, `capability`, `state`,
 * `metric` — as `type: string` with the description *"Repeatable or comma-separated"*. The schema
 * and the prose disagree; the prose is what the server implements, and comma-separation is the
 * form that satisfies both, so it is the form used here.
 *
 * (The disagreement is a defect in the generated specification, reported rather than patched: this
 * directory does not edit the contract. When the core declares these as arrays, delete this and
 * pass the array straight through — `buildQueryString` already repeats keys.)
 */
export const csv = (values: readonly string[] | undefined): string | undefined => {
  const cleaned = (values ?? []).map((value) => value.trim()).filter((value) => value !== '')
  return cleaned.length === 0 ? undefined : cleaned.join(',')
}
